// analytics.js — single, defensive GA4 wrapper for dilatih.co.
// Every analytics call goes through here so a gtag failure can NEVER throw into a
// user flow (especially payment). gtag is loaded in public/index.html.
//
// Revenue note: the `purchase` event is fired SERVER-SIDE from the PAID webhook (any gateway)
// (Measurement Protocol) — see App\Payments\Fulfillment\Ga4Purchase. The client only
// captures the GA client_id/session_id at checkout (getGaClientId/getGaSessionId) and
// sends them with the create-transaction request so the server hit inherits attribution.

const GA_ID = process.env.VUE_APP_GOOGLE_ANALYTIC || "G-QBVNPEB09X";

const CONSENT_KEY = "dl_analytics_consent";

/** The visitor's analytics choice: "granted", "denied", or null (not chosen yet; the notice keeps showing).
 *  Analytics runs unless it is "denied" (owner, 2026-10-29: staying on the site without choosing is accepting;
 *  the default is in public/index.html). */
export function analyticsConsent() {
  try {
    const v = window.localStorage.getItem(CONSENT_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch (e) {
    return null;
  }
}

/** Remove the GA cookies (_ga, _ga_<id>) GA set while analytics was on. */
function clearGaCookies() {
  try {
    const host = window.location.hostname;
    const domains = ["", host, "." + host.replace(/^www\./, "")];
    document.cookie
      .split("; ")
      .map((c) => c.split("=")[0])
      .filter((name) => name === "_ga" || name.startsWith("_ga_"))
      .forEach((name) =>
        domains.forEach((d) => {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? "; domain=" + d : ""}`;
        })
      );
  } catch (e) {
    /* no-op */
  }
}

/** Store the choice and tell gtag (Consent Mode); "denied" also removes the GA cookies already set. */
export function setAnalyticsConsent(value) {
  try {
    window.localStorage.setItem(CONSENT_KEY, value);
  } catch (e) {
    /* private mode: the choice lasts this visit only */
  }
  try {
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", { analytics_storage: value });
    }
  } catch (e) {
    /* analytics must never break the app */
  }
  if (value === "denied") clearGaCookies();
}

/** Fire a GA4 event. Safe: swallows any error. */
export function track(event, params = {}) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", event, params);
    }
  } catch (e) {
    /* analytics must never break the app */
  }
}

/** Register an SPA page_view on route change. GA gets page_location built from `path`, never the raw address
 *  bar (it can hold a reset token or an email; see router meta.analyticsPath): `set` makes every later event
 *  (scroll, clicks) report it too. The automatic page_view on load is off in public/index.html for the same reason. */
export function trackPageView(path, title) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      const page = { page_location: window.location.origin + path, page_title: title || document.title };
      window.gtag("set", page);
      window.gtag("event", "page_view", page);
    }
  } catch (e) {
    /* no-op */
  }
}

function readCookie(name) {
  try {
    const m = document.cookie.match("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)");
    return m ? decodeURIComponent(m[1]) : "";
  } catch (e) {
    return "";
  }
}

/** GA4 client_id from the `_ga` cookie (GA1.1.<a>.<b> -> "<a>.<b>"). "" if absent. */
export function getGaClientId() {
  const v = readCookie("_ga"); // e.g. GA1.1.1234567890.1700000000
  if (!v) return "";
  const parts = v.split(".");
  return parts.length >= 4 ? parts.slice(-2).join(".") : "";
}

/** GA4 session_id from the `_ga_<CONTAINER>` cookie. "" if absent.
 *  Old layout GS1.1.<session_id>.<n>..., current layout GS2.1.s<session_id>$o<n>$g<n>$t<ts>...
 *  CONTAINER = measurement id without the leading "G-" (e.g. G-QBVNPEB09X -> QBVNPEB09X). */
export function getGaSessionId() {
  const v = readCookie("_ga_" + GA_ID.replace(/^G-/, ""));
  if (!v) return "";
  const m = /^GS\d\.\d\.s?(\d+)/.exec(v);
  return m ? m[1] : "";
}

export default { track, trackPageView, getGaClientId, getGaSessionId };
